import {
    FilterDefinition,
    getFilterColumnId,
    UNASSIGNED_FILTER_OPTION_ID,
    UNKNOWN_VALUE_LABEL,
} from '../../../shared/utils/tableFilters'
import { createFilterBuilders } from '../../../shared/utils/tableFilterFactories'
import { getDateLabel, toListLabel } from '../../../shared/utils/displayValue'
import { ALWAYS_VISIBLE_VALUE_MESSAGE_COLUMN_IDS, VALUE_MESSAGE_STATUSES } from './ValueMessageTable.constants'
import { ValueMessageColumnId, ValueMessageDisplayNames, ValueMessageRow } from './ValueMessageTable.types'

const getStatusOptionId = (row: ValueMessageRow) => row.status || UNASSIGNED_FILTER_OPTION_ID

const createValueMessageStatusFilter = (): FilterDefinition<ValueMessageRow> => ({
    id: 'status',
    label: 'Status',
    getOptions: () => [
        ...VALUE_MESSAGE_STATUSES.map((status, index) => ({ id: status.id, label: status.id, order: index })),
        { id: UNASSIGNED_FILTER_OPTION_ID, label: UNKNOWN_VALUE_LABEL, order: VALUE_MESSAGE_STATUSES.length },
    ],
    matches: (row, selectedOptionIds) => selectedOptionIds.includes(getStatusOptionId(row)),
})

export const getValueMessageFilterDefinitions = (
    { valueNarrative, evidence, evidenceGaps, activities, strategicImperatives, tpp }: ValueMessageDisplayNames,
    defaultVisibleColumnIds: ReadonlySet<ValueMessageColumnId>
): FilterDefinition<ValueMessageRow>[] => {
    const { isHiddenByDefault, textFilter, multiValueFilter, yesNoFilter, linkedFilter, yearFilter, columnToggle } =
        createFilterBuilders<ValueMessageRow, ValueMessageColumnId>(defaultVisibleColumnIds)

    const definitions: FilterDefinition<ValueMessageRow>[] = [
        columnToggle('name', 'Title'),
        { ...createValueMessageStatusFilter(), defaultHidden: isHiddenByDefault('status') },
        textFilter('category', 'Category', (row) => row.categoryName),
        textFilter('narrative', valueNarrative, (row) => row.narrativeName),
        textFilter('priority', 'Priority', (row) => row.priority),
        columnToggle('description', 'Description'),
        multiValueFilter('audiences', 'Audiences', (row) => row.audienceNames),
        multiValueFilter('internalStakeholders', 'Internal Stakeholders', (row) => row.internalStakeholderNames),
        multiValueFilter('localMarkets', 'Local Markets', (row) => row.localMarkets),
        yesNoFilter('isCountrySpecific', 'Country Specific', (row) => !!row.isCountrySpecific),
        yesNoFilter('isInternal', 'Internal', (row) => !!row.isInternal),
        linkedFilter('linkedEvidenceCount', `Linked ${evidence}`, (row) => row.linkedEvidenceCount),
        linkedFilter('primaryEvidenceCount', `Primary ${evidence}`, (row) => row.primaryEvidenceCount),
        linkedFilter(
            'keyEvidenceStatementCount',
            'Linked Key Evidence Statements',
            (row) => row.keyEvidenceStatementCount
        ),
        linkedFilter('evidenceGapCount', evidenceGaps, (row) => row.evidenceGapCount),
        linkedFilter('linkedActivityCount', `Linked ${activities}`, (row) => row.linkedActivityCount),
        linkedFilter(
            'linkedStrategicImperativeCount',
            `Linked ${strategicImperatives}`,
            (row) => row.linkedStrategicImperativeCount
        ),
        linkedFilter('linkedTppCellCount', `Linked ${tpp} Cells`, (row) => row.linkedTppCellCount),
        yearFilter('createdAt', 'Created', (row) => row.createdAtValue),
        yearFilter('updatedAt', 'Updated', (row) => row.updatedAtValue),
    ]

    return definitions.filter(
        (definition) => !ALWAYS_VISIBLE_VALUE_MESSAGE_COLUMN_IDS.has(getFilterColumnId(definition))
    )
}

export const getValueMessageSearchValues = (row: ValueMessageRow) => ({
    name: row.name,
    category: row.categoryName,
    narrative: row.narrativeName,
    priority: row.priority,
    description: row.description,
    audiences: toListLabel(row.audienceNames),
    internalStakeholders: toListLabel(row.internalStakeholderNames),
    localMarkets: toListLabel(row.localMarkets),
    createdAt: getDateLabel(row.createdAtValue),
    updatedAt: getDateLabel(row.updatedAtValue),
})
